/**
 * Magic Link Login Component
 * Replaces password-based LoginScreen
 */
import { useState, useCallback, useEffect } from 'react';

interface MagicLinkLoginProps {
  onAuthenticated: () => void;
}

type LoginState = 'email' | 'sent' | 'checking';

export function MagicLinkLogin({ onAuthenticated }: MagicLinkLoginProps) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<LoginState>('checking');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.authenticated) {
        onAuthenticated();
      } else {
        setState('email');
      }
    } catch {
      setState('email');
    }
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }

    // Client-side domain validation
    if (!trimmedEmail.endsWith('@jpmorgan.com')) {
      setError('Only @jpmorgan.com email addresses are allowed');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setState('sent');
      } else {
        setError(data.error || 'Failed to send magic link');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && state === 'email') {
      handleSubmit(e);
    }
  }, [handleSubmit, state]);

  // Loading state while checking session
  if (state === 'checking') {
    return (
      <div className="h-screen flex items-center justify-center bg-term-bgDark">
        <div className="text-term-textDim">Checking session...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-term-bgDark">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-term-border bg-term-bgMedium">
        <div className="flex items-center gap-3">
          <span className="text-term-accent font-bold text-lg">JPM Evaluation Platform</span>
          <span className="text-term-textDim">|</span>
          <span className="text-term-textDim">Diligence Engine</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="term-box p-6">
            {state === 'sent' ? (
              // Magic link sent confirmation
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-term-green text-3xl mb-3">✉️</div>
                  <h2 className="text-term-text font-bold text-lg">Check Your Email</h2>
                  <p className="text-term-textDim text-sm mt-3">
                    We've sent a magic link to:
                  </p>
                  <p className="text-term-accent font-mono mt-1">{email}</p>
                </div>

                <div className="bg-term-bgDark border border-term-border rounded p-4 text-sm">
                  <p className="text-term-textDim">
                    <span className="text-term-yellow">Note:</span> The link expires in 10 minutes.
                    Check your spam folder if you don't see it.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setState('email');
                    setEmail('');
                  }}
                  className="w-full text-term-textDim text-sm hover:text-term-text transition-colors"
                >
                  Use a different email address
                </button>
              </div>
            ) : (
              // Email input form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-center mb-4">
                  <h2 className="text-term-text font-bold text-lg">Sign In</h2>
                  <p className="text-term-textDim text-sm mt-1">
                    Enter your JPMorgan email to receive a magic link
                  </p>
                </div>

                <div>
                  <label className="block text-term-textDim text-sm mb-2">
                    <span className="text-term-green">$</span> Email address:
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-term-bgDark border border-term-border rounded px-3 py-2
                             text-term-text font-mono focus:outline-none focus:border-term-accent
                             placeholder-term-textDim"
                    placeholder="your.name@jpmorgan.com"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="text-term-red text-sm font-mono">
                    <span className="text-term-red">✗</span> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-term-accent text-term-bgDark font-bold py-2 px-4 rounded
                           hover:bg-term-accentBright transition-colors disabled:opacity-50
                           disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send Magic Link'}
                </button>

                <p className="text-term-textDim text-xs text-center">
                  Only @jpmorgan.com email addresses are accepted
                </p>
              </form>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-term-border text-center">
              <p className="text-term-textDim text-xs">
                Authorized evaluators only. All access is logged.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Status Bar */}
      <footer className="px-4 py-1 bg-term-bgMedium border-t border-term-border text-xs">
        <div className="flex justify-between text-term-textDim">
          <span>Secure Connection</span>
          <span>&copy; 2026 W4M.ai - Diligence Engine</span>
        </div>
      </footer>
    </div>
  );
}
